<template>
    <div>
        <v-form v-model="valid" ref="form" @submit.prevent="submit">
            <v-row>
                <v-col>
                    <h1 :class="this.$vuetify.breakpoint.smAndDown ? 'text-h5 text-center' : 'secondary--text text-h2'">
                        Complete Your Subscription
                    </h1>
                    <h3 class="secondary--text text-h5">
                        Enter your billing details below to activate your plan.
                    </h3>
                </v-col>
            </v-row>

            <v-row v-if="plan">
                <v-col cols="12">
                    <div class="plan-summary-card d-flex align-center justify-space-between flex-wrap">
                        <div class="d-flex align-center">
                            <v-avatar color="white" size="56" class="plan-summary-icon mr-4">
                                <v-icon color="primary" size="28">mdi-message-text</v-icon>
                            </v-avatar>
                            <div>
                                <span class="plan-summary-eyebrow">MESSAGE IQ</span>
                                <h2 class="white--text text-h5 font-weight-bold">{{ plan.name }} Plan</h2>
                                <span v-if="plan.segmentCount" class="white--text text-body-2 plan-summary-sub">
                                    Includes {{ commaSeparateNumber(plan.segmentCount) }} messages / month
                                </span>
                            </div>
                        </div>
                        <div class="plan-price text-h3 white--text d-flex flex-start">
                            <span class="sup font-weight-bold">$</span>
                            <span class="font-weight-bold">{{ splitDecimals(plan.price)[0] }}</span>
                            <div class="d-flex flex-start flex-column">
                                <span class="sup font-weight-bold">{{ splitDecimals(plan.price)[1] }}</span>
                                <span class="sub">{{ plan.frequency }}</span>
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <label class="d-block secondary--text text-caption mb-1">Card Details</label>
                    <div ref="recurly-card" class="recurly-card-outlined"></div>
                    <input type="hidden" name="recurly-token" data-recurly="token">
                    <p v-if="recurlyTokenError !== ''" style="color: red" class="mt-2">{{ recurlyTokenError }}</p>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        label="First Name" data-recurly="first_name" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.firstName" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        label="Last Name" data-recurly="last_name" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.lastName" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        label="Email" data-recurly="email" required type="email"
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.email" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        label="Company" data-recurly="company_name" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.companyName" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="8">
                    <v-text-field
                        label="Address" data-recurly="address1" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.address" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-text-field
                        label="Address 2" data-recurly="address2"
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.address2"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        label="City" data-recurly="city" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.city" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                    <v-select
                        :items="states" label="State" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.state" :rules="requiredRule.select"
                    ></v-select>
                </v-col>
                <v-col cols="6" sm="3">
                    <v-text-field
                        label="Zip" data-recurly="postal_code" required
                        :color="!this.$vuetify.breakpoint.smAndDown ? 'iq_dark' : 'iq_light'"
                        :background-color="!this.$vuetify.breakpoint.smAndDown ? 'iq_light' : ''"
                        outlined
                        v-model="billing.zip" :rules="requiredRule.select"
                    ></v-text-field>
                </v-col>
            </v-row>

            <input type="hidden" value="US" data-recurly="country" />
            <input type="hidden" v-model="billing.state" data-recurly="state" />

            <v-footer :app="this.$vuetify.breakpoint.smAndDown" padless class="px-0 bg-transparent">
                <v-container fluid class="pa-0">
                    <v-row v-if="submitError" no-gutters>
                        <v-col cols="12">
                            <v-alert
                                type="error" prominent border="left" colored-border
                                elevation="3" icon="mdi-alert-circle" class="mb-4"
                            >
                                {{ submitError }}
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12">
                            <v-card-actions class="d-flex justify-space-between px-0">
                                <v-btn
                                    x-large outlined color="primary"
                                    :loading="loading"
                                    :disabled="!valid || !isCardValidClass"
                                    @click="submit"
                                >
                                    Submit Payment<span v-if="plan">: ${{ commaSeparateNumber(plan.price) }}</span>
                                </v-btn>
                            </v-card-actions>
                        </v-col>
                    </v-row>
                </v-container>
            </v-footer>
        </v-form>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import recurly from '@/services/recurly';

export default {
    name: "Payment",
    computed: {
        ...mapGetters('messageIqSignup', { plan: 'getPlan' })
    },
    data: () => ({
        valid: false,
        billing: {
            firstName: "",
            lastName: "",
            email: "",
            companyName: "",
            address: "",
            address2: "",
            city: "",
            state: "",
            zip: ""
        },
        states: [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL',
            'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
            'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
            'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ],
        recurlyTokenError: "",
        submitError: "",
        loading: false,
        requiredRule: {
            select: [(v) => !!v || "Required."]
        },
        isCardValidClass: false,
        cardElement: null
    }),
    mounted() {
        recurly.configure();
        const elements = window.recurly.Elements();
        this.cardElement = elements.CardElement();
        this.cardElement.attach(this.$refs['recurly-card']);
        // CardElement() only creates the field - attach() is what actually mounts its
        // iframe into the container. Recurly Elements reports validity via the
        // documented 'change' event (state.valid), not a CSS class on the container -
        // there is no 'recurly-hosted-field-card'/'-valid' class in this Recurly.js
        // version, so polling for one (as this file previously did) never resolves
        // and permanently disables the submit button.
        this.cardElement.on('change', (state) => {
            this.isCardValidClass = !!state.valid;
        });
    },
    beforeDestroy() {
        if (this.cardElement) {
            this.cardElement.remove();
        }
    },
    methods: {
        ...mapActions('messageIqSignup', ['submitPayment']),
        async submit() {
            this.submitError = "";
            this.recurlyTokenError = "";
            this.loading = true;

            let token;
            try {
                token = await recurly.tokenize(this.$refs.form.$el);
            } catch (err) {
                this.recurlyTokenError = err.message || "There was a problem with your card. Please check the details and try again.";
                this.loading = false;
                return;
            }

            try {
                await this.submitPayment({
                    planId: this.plan.id,
                    firstName: this.billing.firstName,
                    lastName: this.billing.lastName,
                    email: this.billing.email,
                    address: this.billing.address,
                    address2: this.billing.address2,
                    city: this.billing.city,
                    state: this.billing.state,
                    zip: this.billing.zip,
                    token: token,
                    companyName: this.billing.companyName
                });

                this.$router.push('/signup/message-iq/account');
            } catch (err) {
                this.submitError = (err.response && err.response.data && err.response.data.message)
                    || "We were unable to process your payment. Please check your card details and try again.";
                console.error(err);
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.recurly-card-outlined {
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    padding: 18px 12px;
    background-color: #fff;
}

.plan-summary-card {
    background-image: linear-gradient(135deg, #ed0c87 0%, #415a99 100%);
    border-radius: 16px;
    padding: 20px 28px;
    margin-bottom: 24px;
    box-shadow: 0 12px 28px rgba(237, 12, 135, 0.25);
    gap: 16px;

    .plan-summary-icon {
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }

    .plan-summary-eyebrow {
        display: block;
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 1.5px;
        opacity: 0.85;
    }

    h2 {
        margin-bottom: 0;
    }

    .plan-summary-sub {
        display: block;
        opacity: 0.9;
        margin-top: 2px;
    }

    .plan-price {
        opacity: 1;
    }
}
</style>
